import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import setTableColumns from "@insite/client-framework/Store/Pages/ProductList/Handlers/SetTableColumns";
import setVisibleColumnNames from "@insite/client-framework/Store/Pages/ProductList/Handlers/SetVisibleColumnNames";
import {
    getProductListDataView,
    isLoadMore,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { ProductListViewType } from "@insite/client-framework/Store/Pages/ProductList/ProductListState";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardContainerMultiColumn, {
    CardContainerMultiColumnStyles,
} from "@insite/content-library/Components/CardContainerMultiColumn";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import { ProductListPageDataContext } from "@insite/content-library/Pages/ProductListPage";
import ProductListProductContext from "@insite/content-library/Widgets/ProductList/ProductListProductContext";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Hidden from "@insite/mobius/Hidden";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { viewItemList, viewPromotion } from "@WhiteCap/Common/Utillities/CustomTracking";
import setView from "@WhiteCap/Handlers/ProductGrouping/SetViewHandler";
import useProductListView from "@WhiteCap/Hooks/useProductListView";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import GuestProductListProductCard from "@WhiteCap/Widgets/GuestRFQ/GuestProductListProductCard";
import GuestProductListProductGridCard from "@WhiteCap/Widgets/GuestRFQ/GuestProductListProductGridCard";
import GuestProductListTable from "@WhiteCap/Widgets/GuestRFQ/GuestProductListTable";
import ProductListProductCatalog from "@WhiteCap/Widgets/ProductList/ProductListProductCatalog";
import isEqual from "lodash/isEqual";
import sortBy from "lodash/sortBy";
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// EC-234 : SubTask - EC-282 = Creating a PLP variant for Guest User

const enum fields {
    showImage = "showImage",
    showCompare = "showCompare",
    showBrand = "showBrand",
    showTitle = "showTitle",
    showPartNumbers = "showPartNumbers",
    showAvailability = "showAvailability",
    showAttributes = "showAttributes",
    showPrice = "showPrice",
    showAddToList = "showAddToList",
    productListDefaultView = "productListDefaultView",
    showImageBadges = "showImageBadges",
    maximumNumberOfImageBadges = "maximumNumberOfImageBadges",
    showTextBadges = "showTextBadges",
    maximumNumberOfTextBadges = "maximumNumberOfTextBadges",
    maxAttributesToShow = "maxAttributesToShow",
    specialCategoriesName = "specialCategoriesName",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showImage]: boolean;
        [fields.showCompare]: boolean;
        [fields.showTitle]: boolean;
        [fields.showBrand]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showAvailability]: boolean;
        [fields.showAttributes]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAddToList]: boolean;
        [fields.productListDefaultView]: ProductListViewType;
        [fields.showImageBadges]: boolean;
        [fields.maximumNumberOfImageBadges]: number;
        [fields.showTextBadges]: boolean;
        [fields.maximumNumberOfTextBadges]: number;
        [fields.maxAttributesToShow]: number;
        [fields.specialCategoriesName]: string;
    };
}

const mapStateToProps = (state: CustomApplicationState, ownProps: OwnProps) => {
    const {
        pages: {
            productList: {
                isLoading,
                isSearchPage,
                productFilters: { pageCategoryId },
                parameter,
            },
        },
    } = state;
    const {
        websiteSettings: { includeRealTimeInventoryAndPricingForSeo },
        productSettings: { productLoadStyle },
    } = getSettingsCollection(state);
    const view = state.WhiteCap.view.view || ownProps.fields.productListDefaultView;
    let isCatalogViewLoading = isLoading;
    if (view === "Catalog") {
        isCatalogViewLoading = state.WhiteCap.products.isLoading;
    }
    const isAuthenticated = state.context.session.isAuthenticated;
    const categoriesId = state.pages.productList.filteredApiParameter?.categoryId;
    let category = null;

    if (categoriesId) {
        category = state.data.categories.byId[categoriesId];
    }

    return {
        isLoading: isCatalogViewLoading,
        productsDataView: getProductListDataView(state),
        productsWithGrouping: state.WhiteCap.products,
        // EC-849-Create a custom redux store to store the data
        // use custom Product list view
        view,
        isSearchPage,
        attributeTypeFacets: getProductListDataViewProperty(state, "attributeTypeFacets"),
        brandFacets: getProductListDataViewProperty(state, "brandFacets"),
        productLineFacets: getProductListDataViewProperty(state, "productLineFacets"),
        includeRealTimeInventoryAndPricingForSeo,
        pageCategoryId,
        productLoadStyle,
        page: parameter?.page ?? 1,
        pageSize: parameter?.pageSize ?? 0,
        totalItemCount: getProductListDataViewProperty(state, "pagination")?.totalItemCount ?? 0,
        isLoadMore: isLoadMore(state),
        isCatalogViewEnabled: state.context.settings.properties.enableCatalogView === "True",
        isAuthenticated,
        category,
    };
};

const mapDispatchToProps = {
    setView,
    setTableColumns,
    setVisibleColumnNames,
    addProductFilters,
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface GuestProductListCardListStyles {
    wrapper?: InjectableCss;
    cardList?: CardListStyles;
    noProductsText?: TypographyPresentationProps;
    centeringWrapper?: InjectableCss;
    centeringWrapperNoRecFound?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    cardContainerStyles?: CardContainerStyles;
    cardContainerMultiColumnStyles?: CardContainerMultiColumnStyles;
    loadMoreWrapper?: InjectableCss;
    loadMoreButton?: ButtonPresentationProps;
}

export const listStyles: GuestProductListCardListStyles = {
    wrapper: {
        css: css`
            margin-top: 20px;
            border-top: 1px solid ${getColor("common.border")};
        `,
    },
    cardList: {
        gridContainer: {
            gap: 0,
        },
    },
    centeringWrapper: {
        css: css`
            width: 100%;
            height: 300px;
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    centeringWrapperNoRecFound: {
        css: css`
            width: 100%;
            height: 100%;
            margin-top: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noProductsText: {
        variant: "h4",
        css: css`
            display: block;
            margin: auto;
        `,
    },
    cardContainerStyles: {
        cardDivider: {
            css: css`
                width: 100%;
                /* EC-631 : making the size 100% so the UI doesn't shift when MFG/SKU data is not present */
                .product {
                    width: 100%;
                }
            `,
        },
    },
    loadMoreWrapper: {
        css: css`
            width: 100%;
            height: 100px;
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    loadMoreButton: {
        variant: "secondary",
        css: css`
            margin: 0 10px;
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
};

const styles = listStyles;

interface SpecialCategoryContextType {
    specialCategory: string;
}

// Create the context
export const SpecialCategoryContext = createContext<SpecialCategoryContextType | undefined>(undefined);

const GuestProductListCardList = ({
    id,
    isLoading,
    isSearchPage,
    productsDataView,
    productsWithGrouping,
    view,
    fields,
    attributeTypeFacets,
    brandFacets,
    productLineFacets,
    includeRealTimeInventoryAndPricingForSeo,
    pageCategoryId,
    productLoadStyle,
    page,
    pageSize,
    totalItemCount,
    isLoadMore,
    isCatalogViewEnabled,
    isAuthenticated,
    setView,
    setTableColumns,
    setVisibleColumnNames,
    addProductFilters,
    category,
}: Props) => {
    // EC-848- Calling Custom Hook To Check Current View of PLP
    const productListView = useProductListView();
    const [pervProducts, setPrevProduct] = useState<any[]>([]);
    const firedPromotionsRef = useRef<Set<string>>(new Set());
   
       
    useEffect(() => {
        setView({ view });
    }, []);


    useEffect(() => {
        if (productsDataView.isLoading || !productsDataView.value?.length) {
            return;
        }

        const categoryNames = fields.specialCategoriesName
            ? fields.specialCategoriesName.split(",").map(item => item.trim().toLowerCase())
            : [];
        const categoryName = category?.shortDescription?.toLowerCase();
        const isSpecialCategoryPLP =
            categoryName && categoryNames.includes(categoryName);

        let promoItems: any[] = [];

        if (isSpecialCategoryPLP) {
            promoItems = productsDataView.value.map(product => ({
                item_id: product.productNumber,
                item_name: product.productTitle,
                item_brand: product.brand?.name || "",
                price: isAuthenticated ? product.unitListPrice : 0,
                promotion_id: product.badges?.length ? product.badges[0].id : category?.id,
                promotion_name: product.badges?.length ? product.badges[0].name : category?.name,
            }));
        } else {
            promoItems = productsDataView.value
                .filter(product => (product.badges?.length ?? 0) > 0)
                .map(product => ({
                    item_id: product.productNumber,
                    item_name: product.productTitle,
                    item_brand: product.brand?.name || "",
                    price: isAuthenticated ? product.unitListPrice : 0,
                    promotion_id: product.badges?.[0].id || "",
                    promotion_name: product.badges?.[0].name || "",
                }));
        }

        // 🔑 Deduplicate products so same product doesn’t fire multiple times
        const newItems = promoItems.filter(item => !firedPromotionsRef.current.has(item.item_id));

        if (newItems.length > 0) {
            // Mark these as fired
            newItems.forEach(item => firedPromotionsRef.current.add(item.item_id));

            viewPromotion({
                event: "view_promotion",
                ecommerce: {
                    items: newItems,
                },
            });
        }
    }, [productsDataView.value, category?.id]);



    useEffect(() => {
        if (productsDataView.isLoading) {
            return;
        }

        const mapsProducts =
            productsDataView.value &&
            productsDataView.value.map(item => {
                return {
                    item_name: item.productTitle,
                    item_id: item.productNumber,
                    item_brand: item.brand?.name ? item.brand?.name : "",
                };
            });

        const isEqualProducts = isEqual(pervProducts, mapsProducts);

        if (!mapsProducts || isEqualProducts) {
            return;
        }
        if (mapsProducts) {
            setPrevProduct(mapsProducts);
        }
        // Fire GTM event
        viewItemList({
            event: "view_item_list",
            ecommerce: {
                items: mapsProducts,
            },
        });
    }, [productsDataView]);

    useEffect(() => {
        if (!attributeTypeFacets?.length && !brandFacets?.length && !productLineFacets?.length) {
            setTableColumns({ tableColumns: [] });
            setVisibleColumnNames({ visibleColumnNames: [] });
            return;
        }

        const columns = attributeTypeFacets
            ? sortBy(
                  attributeTypeFacets,
                  o => o.sortOrder,
                  o => o.name,
              )
            : [];
        if (brandFacets?.length) {
            columns.push({
                attributeTypeId: "",
                name: "Brand",
                nameDisplay: "",
                sortOrder: 0,
                attributeValueFacets: [],
            });
        }

        if (productLineFacets?.length) {
            columns.push({
                attributeTypeId: "",
                name: "Product Line",
                nameDisplay: "",
                sortOrder: 0,
                attributeValueFacets: [],
            });
        }

        setTableColumns({ tableColumns: columns });
        setVisibleColumnNames({ visibleColumnNames: columns?.slice(0, 3).map(o => o.name) || [] });
    }, [pageCategoryId, attributeTypeFacets?.length, brandFacets?.length, productLineFacets?.length]);

    const observer: React.RefObject<any> = useRef(null);
    const itemsLeft = totalItemCount - page * pageSize;

    const infiniteScrollTriggerRef = useCallback(
        (node: any) => {
            if (productLoadStyle !== "InfiniteScroll" || getIsWebCrawler() || isLoading || itemsLeft <= 0) {
                return;
            }

            if (observer.current) {
                observer.current.disconnect();
            }

            observer.current = new IntersectionObserver(entries => {
                if (entries[0].isIntersecting) {
                    addProductFilters({ page: page + 1 });
                }
            });

            if (node) {
                observer.current.observe(node);
            }
        },
        [productLoadStyle, isLoading, itemsLeft],
    );

    const loadMoreClickHandler = () => {
        addProductFilters({ page: page + 1 });
    };

    if (isLoading && !productsDataView.value && !isLoadMore) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} data-test-selector="guestProductListCardListSpinner" />
            </StyledWrapper>
        );
    }

    if (!productsDataView.value) {
        return null;
    }

    const products = productsDataView.value;
    const productsWithGroupedCat = productsWithGrouping.products;

    if (products.length === 0) {
        const styledWrapper =
            totalItemCount === 0 && !productsDataView.searchTermRedirectUrl? (
                <StyledWrapper {...styles.wrapper}>
                    <StyledWrapper {...styles.centeringWrapperNoRecFound}>{siteMessage("noRecFound")}</StyledWrapper>

                    <StyledWrapper {...styles.centeringWrapperNoRecFound} data-test-selector="productListNoneFound">
                        {isSearchPage && <Zone contentId={id} zoneName="Content00" />}
                        {!isSearchPage && <Zone contentId={id} zoneName="Content01" />}
                    </StyledWrapper>
                </StyledWrapper>
            ) : (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} data-test-selector="productListCardListSpinner" />
                </StyledWrapper>
            );

        return styledWrapper;
    }

    if (isCatalogViewEnabled && !isSearchPage) {
        if (!productsWithGroupedCat || (productsWithGroupedCat.length === 0 && view === "Catalog")) {
            const styledWrapper =
                productsWithGroupedCat?.length === 0 ? (
                    <StyledWrapper {...styles.wrapper}>
                        <StyledWrapper {...styles.centeringWrapperNoRecFound}>
                            {siteMessage("noRecFound")}
                        </StyledWrapper>

                        <StyledWrapper {...styles.centeringWrapperNoRecFound} data-test-selector="productListNoneFound">
                            {isSearchPage && <Zone contentId={id} zoneName="Content00" />}
                            {!isSearchPage && <Zone contentId={id} zoneName="Content01" />}
                        </StyledWrapper>
                    </StyledWrapper>
                ) : (
                    <StyledWrapper {...styles.centeringWrapper}>
                        <LoadingSpinner {...styles.spinner} data-test-selector="productListCardListSpinner" />
                    </StyledWrapper>
                );

            return styledWrapper;
        }
    }

    const renderProducts = (children: ReactNode) => {
        return products.map((product, idx) => (
            <ProductListProductContext product={product} idx={idx} key={product.id}>
                <SpecialCategoryContext.Provider value={{ specialCategory: fields.specialCategoriesName }}>
                    {children}
                </SpecialCategoryContext.Provider>
            </ProductListProductContext>
        ));
    };

    const overriddenProps = { ...fields };
    if (getIsWebCrawler() && !includeRealTimeInventoryAndPricingForSeo) {
        overriddenProps.showAvailability = false;
    }

    return (
        <StyledWrapper
            // EC- 848 :- checking current view based on it adding border
            css={css`
                margin-top: 20px;
                ${productListView === "Catalog" ? "" : `border-top: 1px solid ${getColor("common.border")};`}
            `}
        >
            <ProductListPageDataContext.Consumer>
                {({ ref }) => {
                    return ref ? <span ref={ref} tabIndex={-1} /> : undefined;
                }}
            </ProductListPageDataContext.Consumer>
            <LoadingOverlay loading={isLoading && !isLoadMore}>
                <Hidden below="md">
                    <CardList extendedStyles={styles.cardList} data-test-selector={`productListCards_${view}`}>
                        {view === "Table" ? (
                            <GuestProductListTable products={products} {...overriddenProps} />
                        ) : view === "Catalog" && !isSearchPage ? (
                            // Separate JSX for Catalog View to avoid duplication
                            <ProductListProductCatalog
                                maxAttrToShow={fields.maxAttributesToShow}
                                productsWithGroupedCat={productsWithGroupedCat as ProductModel[]}
                                {...overriddenProps}
                            />
                        ) : (
                            renderProducts(
                                view === "List" ? (
                                    <CardContainer extendedStyles={styles.cardContainerStyles}>
                                        <GuestProductListProductCard {...overriddenProps} />
                                    </CardContainer>
                                ) : (
                                    <CardContainerMultiColumn extendedStyles={styles.cardContainerMultiColumnStyles}>
                                        <GuestProductListProductGridCard {...overriddenProps} />
                                    </CardContainerMultiColumn>
                                ),
                            )
                        )}
                    </CardList>
                </Hidden>
                <Hidden above="sm">
                    <CardList extendedStyles={styles.cardList} data-test-selector="cardListProductsNarrow">
                        {renderProducts(
                            <CardContainer extendedStyles={styles.cardContainerStyles}>
                                <GuestProductListProductCard {...overriddenProps} />
                            </CardContainer>,
                        )}
                    </CardList>
                </Hidden>
                {!isLoading && isLoadMore && productLoadStyle === "InfiniteScroll" && itemsLeft > 0 && (
                    <div ref={infiniteScrollTriggerRef} data-test-selector="infiniteScrollTrigger"></div>
                )}
                {!isLoading && isLoadMore && productLoadStyle === "LoadMore" && itemsLeft > 0 && (
                    <StyledWrapper {...styles.loadMoreWrapper}>
                        <Button
                            {...styles.loadMoreButton}
                            onClick={loadMoreClickHandler}
                            data-test-selector="loadMoreButton"
                        >
                            {translate("Load More")}
                        </Button>
                    </StyledWrapper>
                )}
                {isLoading && isLoadMore && (
                    <StyledWrapper {...styles.centeringWrapper}>
                        <LoadingSpinner {...styles.spinner} />
                    </StyledWrapper>
                )}
            </LoadingOverlay>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(GuestProductListCardList),
    definition: {
        group: "Product List",
        displayName: "Guest RFQ Card List",
        allowedContexts: ["ProductListPage"],
        fieldDefinitions: [
            {
                name: "customNote",
                displayName: "Important Note:- Selections are not applicable for Catalog view.",
                editorTemplate: "LabelField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 0,
                isVisible: widget => widget.fields.productListDefaultView !== "Catalog",
            },
            {
                name: fields.showImage,
                displayName: "Show Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
                isVisible: widget => widget.fields.productListDefaultView !== "Catalog",
            },
            {
                name: fields.showCompare,
                displayName: "Show Compare",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
                isVisible: widget => widget.fields.productListDefaultView !== "Catalog",
            },
            {
                name: fields.showBrand,
                displayName: "Show Brand",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
                isVisible: widget => widget.fields.productListDefaultView !== "Catalog",
            },
            {
                name: fields.showTitle,
                displayName: "Show Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
                isVisible: widget => widget.fields.productListDefaultView !== "Catalog",
            },
            {
                name: fields.showPartNumbers,
                displayName: "Show Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
                isVisible: widget => widget.fields.productListDefaultView !== "Catalog",
            },
            {
                name: fields.showAvailability,
                displayName: "Show Availability",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
                isVisible: widget => widget.fields.productListDefaultView !== "Catalog",
            },
            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
                isVisible: widget => widget.fields.productListDefaultView !== "Catalog",
            },
            {
                name: fields.showPrice,
                displayName: "Show Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
                isVisible: widget => widget.fields.productListDefaultView !== "Catalog",
            },
            {
                name: fields.showAddToList,
                displayName: "Show Add To List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 9,
                isVisible: widget => widget.fields.productListDefaultView !== "Catalog",
            },
            {
                name: fields.productListDefaultView,
                editorTemplate: "RadioButtonsField",
                defaultValue: "List",
                fieldType: "General",
                sortOrder: 10,
                options: [
                    {
                        displayName: "List",
                        value: "List",
                    },
                    {
                        displayName: "Grid",
                        value: "Grid",
                    },
                    {
                        displayName: "Table",
                        value: "Table",
                    },
                    {
                        displayName: "Catalog",
                        value: "Catalog",
                    },
                ],
            },
            {
                name: fields.showImageBadges,
                displayName: "Show Image Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 11,
                isVisible: widget => widget.fields.productListDefaultView !== "Catalog",
            },
            {
                name: fields.maximumNumberOfImageBadges,
                editorTemplate: "DropDownField",
                isVisible: widget =>
                    widget.fields.showImageBadges && widget.fields.productListDefaultView !== "Catalog",
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "General",
                sortOrder: 12,
            },
            {
                name: fields.showTextBadges,
                displayName: "Show Text Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 13,
                isVisible: widget => widget.fields.productListDefaultView !== "Catalog",
            },
            {
                name: fields.maximumNumberOfTextBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showTextBadges && widget.fields.productListDefaultView !== "Catalog",
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "General",
                sortOrder: 14,
            },
            {
                name: fields.maxAttributesToShow,
                displayName: "Maximum Attributes to show for Catalog View",
                editorTemplate: "TextField",
                defaultValue: "3",
                fieldType: "General",
                sortOrder: 15,
            },

            {
                name: fields.specialCategoriesName,
                displayName: "Special Categories Name",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 16,
            },
        ],
    },
};

export default widgetModule;

// EC-234 : SubTask - EC-282 = Creating a PLP variant for Guest User
